import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import { useMemo } from 'react';
import { useFilterOptions } from './useFilterOptions';

interface Props {
  selectedItems: AssortmentItemMinimal[];
}

export function useBulkEditSelectedItemsFilterOptions({ selectedItems }: Props) {
  const { suppliers, productGroupHierarchy, productGroups, discountGroups } = useFilterOptions();

  const filteredSuppliers = useMemo(() => {
    const supplierIds = new Set(selectedItems.map((item) => item.supplier.id));
    return suppliers.filter((supplier) => supplierIds.has(supplier.supplierNumber));
  }, [selectedItems, suppliers]);

  const filteredDiscountGroups = useMemo(() => {
    const discountGroupIds = new Set(selectedItems.map((item) => item.discountGroup?.id));
    return discountGroups.filter((group) => discountGroupIds.has(group.id));
  }, [discountGroups, selectedItems]);

  const filteredProductGroups = useMemo(() => {
    const productGroupIds = new Set(selectedItems.map((item) => item.productGroup.groupNumber));
    return productGroupHierarchy
      .map((overGroup) => {
        return {
          ...overGroup,
          subGroups: (overGroup.subGroups ?? [])
            .map((mainGroup) => {
              const productGroups = (mainGroup.subGroups ?? []).filter((productGroup) =>
                productGroupIds.has(productGroup.groupNumber)
              );
              return {
                ...mainGroup,
                subGroups: productGroups,
              };
            })
            .filter(
              (mainGroup) =>
                mainGroup.subGroups.length > 0 || productGroupIds.has(mainGroup.groupNumber)
            ),
        };
      })
      .filter(
        (overGroup) => overGroup.subGroups.length > 0 || productGroupIds.has(overGroup.groupNumber)
      );
  }, [productGroupHierarchy, selectedItems]);

  return {
    suppliers: filteredSuppliers,
    discountGroups: filteredDiscountGroups,
    productGroups,
    productGroupHierarchy: filteredProductGroups,
  };
}
